import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Shield, Mail, Calendar, 
  Edit, Key, Trash2, RefreshCw, Eye, Lock,
  Unlock, UserCheck, UserX, Activity, BarChart3,
  Copy, AlertCircle, ExternalLink, CheckCircle, 
  XCircle, Users, Clock, Globe, Settings, Bell,
  ShieldCheck, KeyRound, LogOut, TrendingUp,
  Download, MoreVertical, Filter, Search,
  ChevronDown, ChevronRight, ChevronLeft
} from 'lucide-react';
import { AdminAuthAPI, AdminAPIUtils } from '../services/api';

function AdminDetail() {
  const { adminId } = useParams();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [activityLogs, setActivityLogs] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [showActivityMenu, setShowActivityMenu] = useState(false);

  useEffect(() => {
    fetchAdmin();
    fetchActivityLogs();
    fetchLoginLogs();
  }, [adminId]);

  const fetchAdmin = async () => {
    try {
      setLoading(true);
      const data = await AdminAuthAPI.getAdmin(adminId);
      setAdmin(data);
      setEditData({
        full_name: data.full_name || '',
        email: data.email || '',
        role: data.role || 'admin',
        status: data.status || 1
      });
    } catch (err) {
      const errorMsg = AdminAPIUtils.handleError(err, false).message;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await AdminAuthAPI.getAdminActivityLogs(adminId, { limit: 10 });
      setActivityLogs(response.logs || []);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchLoginLogs = async () => {
    try {
      const response = await AdminAuthAPI.getAdminLoginLogs(adminId, { limit: 10 });
      setLoginLogs(response.logs || []);
    } catch (err) {
      console.error('Failed to fetch login logs:', err);
    }
  };

  const handleUpdateAdmin = async () => {
    try {
      await AdminAuthAPI.updateAdmin(adminId, editData);
      setEditMode(false);
      fetchAdmin();
      alert('Admin updated successfully!');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const handleResetPassword = async (newPassword) => {
    try {
      await AdminAuthAPI.changeAdminPassword(adminId, newPassword);
      setShowResetModal(false);
      alert('Password reset successfully!');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const handleDeleteAdmin = async () => {
    try {
      await AdminAuthAPI.deleteAdmin(adminId);
      setShowDeleteModal(false);
      navigate('/admins');
      alert('Admin deleted successfully!');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', icon: <ShieldCheck className="w-3 h-3" /> },
      admin: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: <UserCheck className="w-3 h-3" /> },
      support: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: <Settings className="w-3 h-3" /> },
      finance: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: <BarChart3 className="w-3 h-3" /> }
    };
    
    const configItem = config[role] || { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <User className="w-3 h-3" /> };
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {role?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === 1) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
          <UserCheck className="w-3 h-3" />
          Active
        </span>
      );
    } else if (status === 2) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          Banned
        </span>
      );
    }
    return (
      <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-500/10 text-gray-400 border border-gray-500/20 flex items-center gap-1.5">
        <UserX className="w-3 h-3" />
        Inactive
      </span>
    );
  };

  const statCards = [
    {
      title: 'Activity Logs',
      value: activityLogs.length || 0,
      icon: <Activity className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: '24h',
      changeText: 'last activity',
      trend: 'up'
    },
    {
      title: 'Successful Logins',
      value: loginLogs.filter(log => log.success).length || 0,
      icon: <UserCheck className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: '30d',
      changeText: 'total logins',
      trend: 'up'
    },
    {
      title: 'Failed Logins',
      value: loginLogs.filter(log => !log.success).length || 0,
      icon: <AlertCircle className="w-5 h-5" />,
      color: 'from-rose-500 to-red-500',
      change: 'Attempts',
      changeText: 'in last 10',
      trend: 'neutral'
    },
    {
      title: 'Last Active',
      value: admin?.last_login_at ? AdminAPIUtils.timeAgo(admin.last_login_at) : 'Never',
      icon: <Clock className="w-5 h-5" />,
      color: 'from-purple-500 to-violet-500',
      change: 'Status',
      changeText: 'current session',
      trend: admin?.status === 1 ? 'up' : 'down'
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading admin details...</p>
        </div>
      </div>
    );
  }

  if (error || !admin) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center max-w-md">
          <AlertCircle className="w-20 h-20 text-rose-400 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">
            {error ? 'Error Loading Admin' : 'Admin Not Found'}
          </h2>
          <p className="text-charcoal-gray mb-8">
            {error || 'The admin you are looking for does not exist or you do not have permission to view it.'}
          </p>
          <button
            onClick={() => navigate('/admins')}
            className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            Back to Admins
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admins')}
            className="p-2.5 bg-night-blue/50 hover:bg-night-blue border border-gray-800 text-white rounded-xl transition-all duration-300 hover:scale-105"
            title="Back to Admins"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              Admin Details
            </h1>
            <p className="text-charcoal-gray mt-1">View and manage administrator account</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setEditMode(!editMode)}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
          >
            <Edit className="w-4 h-4" />
            {editMode ? 'Cancel Edit' : 'Edit Admin'}
          </button>
          <button
            onClick={fetchAdmin}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {admin.role !== 'super_admin' && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2.5 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Admin Profile Header */}
      <div className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="relative">
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
              {admin.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-night-blue ${
              admin.status === 1 ? 'bg-emerald-500' : 
              admin.status === 2 ? 'bg-rose-500' : 
              'bg-gray-500'
            }`} />
          </div>
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{admin.username}</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-charcoal-gray">ID: {admin.id}</div>
                  {getRoleBadge(admin.role)}
                  {getStatusBadge(admin.status)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(admin.email);
                    alert('Email copied to clipboard!');
                  }}
                  className="p-2.5 bg-gray-800/50 hover:bg-sky-500/20 text-sky-400 hover:text-sky-300 rounded-xl transition-all duration-300 hover:scale-110"
                  title="Copy Email"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowResetModal(true)}
                  className="p-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-110"
                  title="Reset Password"
                >
                  <KeyRound className="w-5 h-5" />
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowActivityMenu(!showActivityMenu)}
                    className="p-2.5 bg-gray-800/50 hover:bg-gray-700 text-gray-400 hover:text-white rounded-xl transition-all duration-300 hover:scale-110"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  {showActivityMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                      <button
                        onClick={() => navigate(`/logs?admin_id=${admin.id}`)}
                        className="w-full px-4 py-2.5 text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-2 transition-colors"
                      >
                        <Activity className="w-4 h-4" />
                        View Full Activity Logs
                      </button>
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/admins/${admin.id}`;
                          navigator.clipboard.writeText(url);
                          alert('Link copied to clipboard!');
                          setShowActivityMenu(false);
                        }}
                        className="w-full px-4 py-2.5 text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Copy Profile Link
                      </button>
                      <div className="px-4 py-2 border-t border-gray-800">
                        <p className="text-xs text-charcoal-gray">Admin ID: {admin.id}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-charcoal-gray" />
                <div>
                  <p className="text-sm text-charcoal-gray">Email</p>
                  <p className="text-white font-medium">{admin.email}</p>
                </div>
              </div>
              {admin.full_name && (
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-charcoal-gray" />
                  <div>
                    <p className="text-sm text-charcoal-gray">Full Name</p>
                    <p className="text-white font-medium">{admin.full_name}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-charcoal-gray" />
                <div>
                  <p className="text-sm text-charcoal-gray">Created</p>
                  <p className="text-white font-medium">
                    {AdminAPIUtils.formatDate(admin.created_at, 'date')}
                  </p>
                </div>
              </div>
              {admin.created_by_username && (
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-charcoal-gray" />
                  <div>
                    <p className="text-sm text-charcoal-gray">Created By</p>
                    <p className="text-white font-medium">{admin.created_by_username}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">Active</span>
                </div>
              )}
              {card.trend === 'down' && (
                <div className="flex items-center text-rose-400 bg-rose-400/10 px-3 py-1 rounded-full">
                  <span className="text-sm font-medium">Inactive</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className="font-medium text-gray-300">
                  {typeof card.change === 'string' && card.change.startsWith('+') ? '+' : ''}{card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Edit Form & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Edit Form / Admin Info */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-white">Admin Information</h3>
              {editMode && (
                <button
                  onClick={handleUpdateAdmin}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
                >
                  Save Changes
                </button>
              )}
            </div>
            
            {editMode ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                    <div className="font-mono text-white bg-gray-900/50 p-3 rounded-xl border border-gray-800">
                      {admin.username}
                    </div>
                    <p className="text-xs text-charcoal-gray mt-2">Username cannot be changed</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Admin ID</label>
                    <div className="text-white bg-gray-900/50 p-3 rounded-xl border border-gray-800">
                      {admin.id}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    placeholder="admin@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Full Name (Optional)</label>
                  <input
                    type="text"
                    value={editData.full_name || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, full_name: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    placeholder="John Doe"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                    <select
                      value={editData.role}
                      onChange={(e) => setEditData(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      disabled={admin.role === 'super_admin'}
                    >
                      <option value="admin">Admin</option>
                      <option value="support">Support</option>
                      <option value="finance">Finance</option>
                    </select>
                    {admin.role === 'super_admin' && (
                      <p className="text-xs text-amber-400 mt-2">Super admin role cannot be changed</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                    <select
                      value={editData.status}
                      onChange={(e) => setEditData(prev => ({ ...prev, status: parseInt(e.target.value) }))}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                      <option value="2">Banned</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-charcoal-gray" />
                      <label className="text-sm font-medium text-gray-300">Username</label>
                    </div>
                    <div className="text-white font-medium">{admin.username}</div>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-charcoal-gray" />
                      <label className="text-sm font-medium text-gray-300">Admin ID</label>
                    </div>
                    <div className="text-white font-medium">{admin.id}</div>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-charcoal-gray" />
                    <label className="text-sm font-medium text-gray-300">Email Address</label>
                  </div>
                  <div className="text-white font-medium flex items-center gap-2">
                    {admin.email}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(admin.email);
                        alert('Email copied to clipboard!');
                      }}
                      className="text-charcoal-gray hover:text-white transition-colors"
                      title="Copy Email"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {admin.full_name && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-charcoal-gray" />
                      <label className="text-sm font-medium text-gray-300">Full Name</label>
                    </div>
                    <div className="text-white font-medium">{admin.full_name}</div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-charcoal-gray" />
                      <label className="text-sm font-medium text-gray-300">Created Date</label>
                    </div>
                    <div className="text-white font-medium">
                      {AdminAPIUtils.formatDate(admin.created_at, 'datetime')}
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-charcoal-gray" />
                      <label className="text-sm font-medium text-gray-300">Last Login</label>
                    </div>
                    <div className="text-white font-medium">
                      {admin.last_login_at ? AdminAPIUtils.formatDate(admin.last_login_at, 'datetime') : 'Never'}
                    </div>
                  </div>
                </div>
                
                {admin.created_by_username && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-charcoal-gray" />
                      <label className="text-sm font-medium text-gray-300">Created By</label>
                    </div>
                    <div className="text-white font-medium">{admin.created_by_username}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
              <button
                onClick={fetchActivityLogs}
                className="p-2 bg-gray-800/50 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
                title="Refresh Activity"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            {logsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-500"></div>
              </div>
            ) : activityLogs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <p className="text-charcoal-gray">No activity recorded</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activityLogs.slice(0, 5).map((log, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-900/30 rounded-xl hover:bg-gray-900/50 transition-colors">
                    <div className={`p-2 rounded-lg ${
                      log.action_type === 'login' ? 'bg-emerald-500/20' :
                      log.action_type === 'update' ? 'bg-amber-500/20' :
                      log.action_type === 'delete' ? 'bg-rose-500/20' :
                      'bg-blue-500/20'
                    }`}>
                      {log.action_type === 'login' && <UserCheck className="w-4 h-4 text-emerald-400" />}
                      {log.action_type === 'update' && <Edit className="w-4 h-4 text-amber-400" />}
                      {log.action_type === 'delete' && <Trash2 className="w-4 h-4 text-rose-400" />}
                      {!['login', 'update', 'delete'].includes(log.action_type) && <Activity className="w-4 h-4 text-blue-400" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <p className="text-white font-medium">{log.action}</p>
                        <span className="text-xs text-charcoal-gray">
                          {AdminAPIUtils.timeAgo(log.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-charcoal-gray mt-1">{log.details}</p>
                      {log.ip_address && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Globe className="w-3 h-3" />
                          <span>{log.ip_address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {activityLogs.length > 5 && (
                  <button
                    onClick={() => navigate(`/logs?admin_id=${admin.id}`)}
                    className="w-full py-3 text-center text-sky-400 hover:text-sky-300 border border-gray-800 hover:border-sky-500/50 rounded-xl transition-all duration-300 hover:bg-sky-500/10"
                  >
                    View All Activity
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Permissions & Quick Actions */}
        <div className="space-y-6">
          {/* Permissions Card */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-6">Permissions</h3>
            
            {admin.permissions && Object.keys(admin.permissions).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(admin.permissions).map(([module, actions]) => {
                  const actionList = Array.isArray(actions) ? actions : [];
                  
                  return (
                    <div key={module} className="border-b border-gray-800 pb-4 last:border-0 last:pb-0">
                      <h4 className="font-medium text-gray-300 mb-2 capitalize">
                        {module.replace(/_/g, ' ')}
                      </h4>
                      {actionList.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {actionList.map((action) => (
                            <span
                              key={action}
                              className="px-2.5 py-1 bg-gray-800/50 text-gray-300 rounded-lg text-xs font-medium border border-gray-700"
                            >
                              {action}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-charcoal-gray italic">Full access</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <p className="text-charcoal-gray">No specific permissions assigned</p>
                <p className="text-sm text-charcoal-gray mt-2">Default role permissions apply</p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-6">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => setEditMode(true)}
                className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
              >
                <span>Edit Admin</span>
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowResetModal(true)}
                className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
              >
                <span>Reset Password</span>
                <KeyRound className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate(`/logs?admin_id=${admin.id}`)}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
              >
                <span>View Activity Logs</span>
                <Activity className="w-4 h-4" />
              </button>
              {admin.role !== 'super_admin' && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
                >
                  <span>Delete Admin</span>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* System Info */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-6">System Information</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Account Created</span>
                <span className="text-white">
                  {AdminAPIUtils.formatDate(admin.created_at, 'date')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Last Updated</span>
                <span className="text-white">
                  {admin.updated_at ? AdminAPIUtils.formatDate(admin.updated_at, 'date') : 'Never'}
                </span>
              </div>
              {admin.deleted_at && (
                <div className="flex justify-between items-center text-rose-400">
                  <span className="text-rose-400">Deleted At</span>
                  <span>
                    {AdminAPIUtils.formatDate(admin.deleted_at, 'datetime')}
                  </span>
                </div>
              )}
              <div className="pt-4 border-t border-gray-800">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-charcoal-gray">Admin Level</span>
                  <span className={`font-semibold ${
                    admin.role === 'super_admin' ? 'text-purple-400' :
                    admin.role === 'admin' ? 'text-blue-400' :
                    admin.role === 'support' ? 'text-emerald-400' :
                    'text-amber-400'
                  }`}>
                    {admin.role === 'super_admin' ? 'Super Admin' :
                     admin.role === 'admin' ? 'Administrator' :
                     admin.role === 'support' ? 'Support Staff' :
                     'Finance Manager'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetModal && (
        <ResetPasswordModal
          admin={admin}
          onClose={() => setShowResetModal(false)}
          onConfirm={handleResetPassword}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteAdminModal
          admin={admin}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAdmin}
        />
      )}
    </div>
  );
}

// Reset Password Modal Component
function ResetPasswordModal({ admin, onClose, onConfirm }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const validate = () => {
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      await onConfirm(password);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(password);
    setConfirmPassword(password);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-night-blue border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-bold text-white">Reset Password</h3>
              <p className="text-sm text-charcoal-gray mt-1">Change password for {admin.username}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1.5 hover:bg-gray-800/50 rounded-lg"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
            <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Password Reset
            </h4>
            <p className="text-sm text-amber-300/80">
              The admin will need to use the new password for their next login. Make sure to send them the new credentials securely.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-300">
                New Password *
              </label>
              <button
                type="button"
                onClick={generatePassword}
                className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
              >
                Generate Secure Password
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors pr-10"
                placeholder="Minimum 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <Eye className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Confirm Password *
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                placeholder="Confirm password"
              />
            </div>
            
            {error && (
              <div className="text-rose-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    Reset Password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Delete Admin Modal Component
function DeleteAdminModal({ admin, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-night-blue border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-rose-500/10 mb-4">
              <AlertCircle className="h-8 w-8 text-rose-400" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">
              Delete Admin Account
            </h3>
            <p className="text-charcoal-gray mb-6">
              Are you sure you want to delete the admin account <span className="font-semibold text-white">{admin.username}</span>?
              This action cannot be undone.
            </p>
            
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 text-left">
              <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Important Note:
              </h4>
              <ul className="text-sm text-amber-300/80 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-xs mt-1">•</span>
                  <span>This will permanently deactivate the admin account</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-xs mt-1">•</span>
                  <span>All admin permissions will be revoked immediately</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-xs mt-1">•</span>
                  <span>Account data will be preserved for audit purposes</span>
                </li>
                {admin.role === 'super_admin' && (
                  <li className="flex items-start gap-2 font-bold text-rose-400">
                    <span className="text-xs mt-1">•</span>
                    <span>Super admin accounts cannot be deleted</span>
                  </li>
                )}
              </ul>
            </div>
            
            <div className="flex justify-center gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading || admin.role === 'super_admin'}
                className={`px-5 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                  admin.role === 'super_admin'
                    ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white hover:scale-105'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Admin
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDetail;